import { v4 as uuidv4 } from 'uuid'
import {
  cachePatients, cacheAppointments, cacheMedications, cacheServices,
  getOfflinePatients, getOfflineAppointments, getOfflineMedications, getOfflineServices,
} from './offlineDb'
import { addToSyncQueue } from './syncQueue'

const API_BASE = '/api'

export class ApiError extends Error {
  /** DRF field-level errors: { field: ["msg", ...] }. */
  fieldErrors?: Record<string, string[]>
  /** DRF non_field_errors / detail string. */
  nonFieldError?: string
  /** Raw parsed error response body — for custom error shapes (e.g. the
   *  drug_interaction block: { error, interactions, override_field }). */
  data?: any
  constructor(
    public status: number,
    message: string,
    opts?: { fieldErrors?: Record<string, string[]>; nonFieldError?: string; data?: any },
  ) {
    super(message)
    this.name = 'ApiError'
    this.fieldErrors = opts?.fieldErrors
    this.nonFieldError = opts?.nonFieldError
    this.data = opts?.data
  }
}

async function getCsrfToken(): Promise<string> {
  const match = document.cookie.match(/csrftoken=([^;]+)/)
  return match ? match[1] : ''
}

// 401-silent-refresh-retry: per CLAUDE.md API-Client spec.
// Mirrors AuthProvider.doRefresh but inline so polling hooks
// (which never touch AuthProvider) get auto-refresh too.
async function doRefresh(): Promise<boolean> {
  const csrf = await getCsrfToken()
  try {
    const res = await fetch('/api/auth/refresh/', {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { 'X-CSRFToken': csrf } : undefined,
    })
    return res.ok
  } catch {
    return false
  }
}

// Endpoints that can be served from IndexedDB when offline
const OFFLINE_CACHE_MAP: Record<string, {
  cache: (data: Record<string, unknown>[]) => Promise<void>
  read: () => Promise<Record<string, unknown>[]>
}> = {
  '/api/patients/': { cache: cachePatients, read: getOfflinePatients },
  '/api/appointments/': { cache: cacheAppointments, read: getOfflineAppointments },
  '/api/medications/': { cache: cacheMedications, read: getOfflineMedications },
  '/api/services/': { cache: cacheServices, read: getOfflineServices },
}

function getOfflineHandler(url: string) {
  for (const [prefix, handler] of Object.entries(OFFLINE_CACHE_MAP)) {
    if (url === prefix || url.startsWith(prefix + '?')) {
      return handler
    }
  }
  return null
}

export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  _isRetry: boolean = false,
): Promise<T> {
  const url = endpoint.startsWith('/') ? endpoint : `${API_BASE}/${endpoint}`
  const method = options.method ?? 'GET'

  const isFormData = options.body instanceof FormData
  const headers: HeadersInit = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  }

  // Auth is via httpOnly cookie (sent automatically via credentials: 'include')

  // Add CSRF token for mutating requests
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = await getCsrfToken()
    if (csrfToken) {
      (headers as Record<string, string>)['X-CSRFToken'] = csrfToken
    }
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for auth
    })

    // 401-silent-refresh-retry. _isRetry guard prevents infinite loop
    // if refresh itself returns 401.
    if (response.status === 401 && !_isRetry) {
      const refreshed = await doRefresh()
      if (refreshed) {
        return apiFetch<T>(endpoint, options, true)
      }
      // Refresh failed → fall through to throw ApiError(401),
      // AuthProvider picks it up and triggers logout flow.
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      // Handle various error response formats
      let message = `HTTP ${response.status}`
      let fieldErrors: Record<string, string[]> | undefined
      let nonFieldError: string | undefined
      if (typeof errorData.detail === 'string') {
        message = errorData.detail
        nonFieldError = errorData.detail
      } else if (typeof errorData.detail === 'object' && errorData.detail !== null) {
        // Could be validation error with field names
        message = JSON.stringify(errorData.detail)
      } else if (errorData.message) {
        message = errorData.message
      } else if (errorData && typeof errorData === 'object') {
        // DRF field-level errors like { "body_part": ["This field may not be blank."] }.
        // Keep the structured shape on the ApiError so the UI can localize +
        // surface per-field; build a flat message for non-structured consumers.
        fieldErrors = {}
        for (const [key, value] of Object.entries(errorData)) {
          if (key === 'alternatives') continue
          const arr = Array.isArray(value) ? value.map(String) : [String(value)]
          if (key === 'non_field_errors') nonFieldError = arr.join(' ')
          else fieldErrors[key] = arr
        }
        if (Object.keys(fieldErrors).length === 0) fieldErrors = undefined
        const parts: string[] = []
        if (nonFieldError) parts.push(nonFieldError)
        if (fieldErrors) {
          for (const [k, v] of Object.entries(fieldErrors)) parts.push(`${k}: ${v.join(', ')}`)
        }
        if (parts.length) message = parts.join('; ')
      }
      throw new ApiError(response.status, message, { fieldErrors, nonFieldError, data: errorData })
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return {} as T
    }

    const data = await response.json()

    // Cache successful GET responses for offline use
    if (method === 'GET') {
      const handler = getOfflineHandler(url)
      if (handler) {
        const items = Array.isArray(data) ? data : data?.results ?? null
        if (items) {
          handler.cache(items).catch(() => { /* silent — caching is best-effort */ })
        }
      }
    }

    return data
  } catch (error) {
    // Offline fallback
    if (!navigator.onLine && error instanceof TypeError) {
      if (method === 'GET') {
        // Try IndexedDB for known endpoints
        const handler = getOfflineHandler(url)
        if (handler) {
          const cached = await handler.read()
          return { results: cached, _offline: true } as T
        }
      } else if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
        // Queue mutation for later sync with idempotency key
        const idempotencyKey = uuidv4()
        await addToSyncQueue({
          url,
          method,
          body: typeof options.body === 'string' ? options.body : null,
        }, idempotencyKey)
        return { _offline: true } as T
      }
    }
    throw error
  }
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string) => apiFetch<T>(endpoint),
  
  post: <T>(endpoint: string, data: unknown) =>
    apiFetch<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  put: <T>(endpoint: string, data: unknown) =>
    apiFetch<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  patch: <T>(endpoint: string, data: unknown) =>
    apiFetch<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: <T>(endpoint: string) =>
    apiFetch<T>(endpoint, { method: 'DELETE' }),

  postForm: <T>(endpoint: string, formData: FormData) =>
    apiFetch<T>(endpoint, {
      method: 'POST',
      body: formData,
    }),

  patchForm: <T>(endpoint: string, formData: FormData) =>
    apiFetch<T>(endpoint, {
      method: 'PATCH',
      body: formData,
    }),
}
